import { useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import {
  selectBookingsState,
  addSnapshot,
  clearSnapshot,
  selectLoggedInUserState,
  filtersModalRulesBookingsChange,
  resetModalBookingsChange,
  filtersModalConditionBookingsChange,
} from '@store';
import { bookingsServices } from '@services';
import { getCleanedUrlParams, showError } from '@utils';
import { FILTER_OPERATOR_ENUMS, FILTER_RULE_ENUMS } from '@constants';
import convertPlate from 'utils/convertPlate';

import { useLayout } from 'hooks/layout';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useAssignees } from 'hooks/common';
import {
  getBookingFields,
  getBookingsCardColumns,
  getBookingsPayload,
  handleBookingLabels,
  handleBookings,
  handleCustomerTypesEnums,
} from './utils';

export const BOOKINGS_PAGE_SIZE = 100;

export const useBookingsOperations = (fetchOnMount = true) => {
  const loggedInUser = useSelector(selectLoggedInUserState);
  const { query, isReady } = useRouter();

  const {
    assignees,
    assigneesWithNone,
    assigneesMap,
    isFetching: isAssigneesFetching,
  } = useAssignees({
    // This is required because useConsolidateStoreOperations calls useBookingsOperations on mount, so assignees were fetching on login page
    enabled: fetchOnMount && isReady,
  });

  const { bookingSnapshot, filters } = useSelector(selectBookingsState);
  const {
    page = 1,
    pageSize = BOOKINGS_PAGE_SIZE,
    orderBy = 'created_at',
    order = 'desc',
    origin,
    contractType,
    contractPeriod,
    stage,
    assignee,
    labels,
    customerTypes,
    query: searchQuery = '',
    dateFrom,
    dateTo,
  } = query;
  const { tenant } = useLayout();
  const dispatch = useDispatch();

  const cleanParams = getCleanedUrlParams({
    page,
    orderBy,
    order,
    tenant,
    pageSize: parseInt(pageSize, 10) || BOOKINGS_PAGE_SIZE,
    origin,
    contract_type: contractType,
    stage,
    assignee,
    query: searchQuery,
    contract_period: contractPeriod,
    'ticket_details.assignee': assignee,
    labels,
    customerTypes,
    dateFrom,
    dateTo,
  });

  const bookingsResponse = useQuery(
    [
      QUERY_KEYS.GET_BOOKINGS_KEY,
      { cleanParams, filters: filters.modalFiltersRules },
    ],
    () => {
      const params = getCleanedUrlParams({
        page,
        orderBy,
        order,
        pageSize: parseInt(pageSize, 10) || BOOKINGS_PAGE_SIZE,
        query: searchQuery,
      });

      const payload = getBookingsPayload(
        cleanParams,
        filters.modalFiltersRules
      );

      return bookingsServices.getBookings({ params, payload });
    },
    { enabled: fetchOnMount && isReady }
  );

  const labelsResponse = useQuery(
    [QUERY_KEYS.GET_BOOKING_LABELS_KEY],
    () =>
      bookingsServices.getBookingLabels({
        params: { page: 1, pageSize: 200 },
        payload: {
          type: 0,
          field: null,
          operator: null,
          value: [
            {
              field: 'is_disabled',
              operator: FILTER_OPERATOR_ENUMS['='],
              type: FILTER_RULE_ENUMS.ComparableRuleType,
              value: false,
            },
          ],
        },
      }),
    { enabled: fetchOnMount && isReady }
  );

  const searchPlates = useCallback(async (value) => {
    try {
      const response = await bookingsServices.getBookingsFilters({
        params: { page: 1, pageSize: 50 },
        payload: {
          field: 'shared_vehicle.plate',
          operator: FILTER_OPERATOR_ENUMS.beginsWith,
          type: FILTER_RULE_ENUMS.CompareStringRuleType,
          value: convertPlate(value),
        },
      });

      return response?.data?.map((task) => ({
        value: task.key,
        text: task.key,
      }));
    } catch (error) {
      showError('Error searching for plates');
      return [];
    }
  }, []);

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOOKINGS_ENUMS_KEY],
    () => bookingsServices.getBookingsEnums(),
    { enabled: fetchOnMount && isReady }
  );

  const customerTypesOptions = useMemo(
    () => handleCustomerTypesEnums(enumsResponse?.data?.data || {}),
    [enumsResponse?.data?.data]
  );

  const bookingLabelsOptions = useMemo(
    () => handleBookingLabels(labelsResponse?.data?.data || []),
    [labelsResponse?.data?.data]
  );

  const handleAddSnapshot = useCallback(
    async (payload) => {
      dispatch(addSnapshot(payload));
    },
    [dispatch]
  );

  const handleClearSnapshot = useCallback(async () => {
    dispatch(clearSnapshot());
  }, [dispatch]);

  const handleChangeRules = (rules) => {
    dispatch(filtersModalRulesBookingsChange(rules));
  };

  const resetModalChange = useCallback(() => {
    dispatch(resetModalBookingsChange());
  }, [dispatch]);

  const handleFiltersModalChange = useCallback(
    (conditions) => {
      dispatch(filtersModalConditionBookingsChange(conditions));
    },
    [dispatch]
  );

  useEffect(() => {
    if (bookingsResponse?.error) {
      showError(bookingsResponse?.error);
    }
  }, [bookingsResponse?.error]);
  const enumsData = enumsResponse?.data?.data ?? {};

  const bookings = handleBookings(
    bookingsResponse?.data?.data || [],
    assigneesMap,
    enumsData
  );
  const columns = getBookingsCardColumns(bookings || []);

  const enums = {
    ...enumsData,
    products:
      (enumsData?.products ?? [])?.map((product) => ({
        name: product.slug,
        label: product.name,
        id: product.id,
      })) ?? [],
  };

  const customFields = getBookingFields({
    assignees,
    bookingLabelsOptions,
    products: enums?.products || [],
    customerTypesOptions: customerTypesOptions || [],
    searchPlates,
  });

  return {
    page,
    loggedInUser,
    enums,
    total: bookingsResponse?.data?.meta?.total,
    pageSize: parseInt(pageSize, 10) || BOOKINGS_PAGE_SIZE,
    loading:
      bookingsResponse.isFetching ||
      isAssigneesFetching ||
      enumsResponse.isFetching,
    bookings,
    columns,
    assignees: assigneesWithNone,
    bookingSnapshot,
    customerTypesOptions,
    bookingLabelsOptions,
    filters,
    customFields,
    getBookings: bookingsResponse.refetch,
    addSnapshot: handleAddSnapshot,
    clearSnapshot: handleClearSnapshot,
    handleFiltersModalChange,
    handleChangeRules,
    resetModalChange,
  };
};
